import { BodyText } from '@/components/BodyText';
import { Box, Button, TableBody } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { Add } from '@mui/icons-material';
import { HelpIcon } from './HelpIcon';
import { HELP_SECTION_PARAM, SHOW_HELP_PARAM, HELP_VIEW_PARAM } from './helpParameters';
import { HelpLink } from './HelpLink';
import { HelpSection } from './HelpContent';
import {
  StyledTable,
  StyledTableHeadRow,
  StyledTableHeadCell,
  StyledTableRow,
  StyledTableCell,
  TableHead,
} from './HelpTable';

export const HowToUploadView = ({ onBack }: { onBack?: () => void }) => {
  const [, setSearchParameters] = useSearchParams();

  const handleViewFileTypes = () => {
    setSearchParameters(
      previous => {
        const showHelpValue = previous.get(SHOW_HELP_PARAM);
        const newParameters = new URLSearchParams(previous);
        newParameters.set(HELP_SECTION_PARAM, HelpSection.Upload);
        newParameters.set(HELP_VIEW_PARAM, 'fileTypes');
        if (showHelpValue) {
          newParameters.set(SHOW_HELP_PARAM, showHelpValue);
        }
        return newParameters;
      },
      { replace: true }
    );
  };

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
      {onBack && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '12px' }}>
          <Button variant='text' onClick={onBack}>
            Back
          </Button>
        </Box>
      )}
      <Box sx={{ flex: 1 }}>
        <BodyText component='p' sx={{ marginBottom: '12px' }}>
          Upload files from the{' '}
          <HelpLink to='assets' helpSection={HelpSection.Upload}>
            Assets
          </HelpLink>{' '}
          page by clicking{' '}
          <HelpIcon>
            <Add sx={{ fontSize: '16px' }} />
          </HelpIcon>{' '}
          button or by dragging and dropping files into assets table.
        </BodyText>
        <BodyText component='p' sx={{ marginBottom: '12px' }}>
          Each file can be up to 4GB. Uploading folders will preserve structure and filenames, which is important for
          multi-file assemblies.
        </BodyText>
        <BodyText component='p' sx={{ marginBottom: '12px' }}>
          After a file is uploaded, it is processed and indexed as an asset. This makes it reportable, searchable and
          comparable. Processing time varies by file size and complexity. The table below describes processing states.
        </BodyText>
        <StyledTable size='small' sx={{ marginBottom: '12px' }}>
          <TableHead>
            <StyledTableHeadRow>
              <StyledTableHeadCell>State</StyledTableHeadCell>
              <StyledTableHeadCell>Description</StyledTableHeadCell>
            </StyledTableHeadRow>
          </TableHead>
          <TableBody>
            <StyledTableRow>
              <StyledTableCell>
                <BodyText color='text.primary'>Indexed</BodyText>
              </StyledTableCell>
              <StyledTableCell>
                <BodyText color='text.primary'>Asset is available for reporting, matching and comparing.</BodyText>
              </StyledTableCell>
            </StyledTableRow>
            <StyledTableRow isEven>
              <StyledTableCell>
                <BodyText color='text.primary'>Indexing</BodyText>
              </StyledTableCell>
              <StyledTableCell>
                <BodyText color='text.primary'>
                  Asset is being processed or is queued for processing. Only text search is available.
                </BodyText>
              </StyledTableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>
                <BodyText color='text.primary'>Failed to index</BodyText>
              </StyledTableCell>
              <StyledTableCell>
                <BodyText color='text.primary'>Processing failed. Delete and try uploading again.</BodyText>
              </StyledTableCell>
            </StyledTableRow>
            <StyledTableRow isEven>
              <StyledTableCell>
                <BodyText color='text.primary'>Unsupported format</BodyText>
              </StyledTableCell>
              <StyledTableCell>
                <BodyText color='text.primary'>
                  File format is not supported. Check{' '}
                  <Box
                    component='span'
                    sx={{ textDecoration: 'underline', cursor: 'pointer' }}
                    onClick={handleViewFileTypes}
                  >
                    supported file types.
                  </Box>
                </BodyText>
              </StyledTableCell>
            </StyledTableRow>
            <StyledTableRow>
              <StyledTableCell>
                <BodyText color='text.primary'>No 3D data</BodyText>
              </StyledTableCell>
              <StyledTableCell>
                <BodyText color='text.primary'>File has no 3D geometry. Only text search is available.</BodyText>
              </StyledTableCell>
            </StyledTableRow>
          </TableBody>
        </StyledTable>
      </Box>
    </Box>
  );
};
